// A mixer bus graph built from a declarative topology. Buses are plain gain nodes wired into a
// tree that routes up to an optional master compressor and out to the AudioContext destination.
// The game supplies the topology as data — the engine knows no bus names.

/** A bus identifier. The game defines its own set (e.g. "master", "sfx", "sfx.impact", "ui"). */
export type BusId = string;

/** One bus: a gain node whose output feeds `parent` (another bus, or the destination). */
export type BusSpec = {
  id: BusId;
  /** Another bus id, or "destination" (routes through the master compressor if one is set). */
  parent: BusId | "destination";
  /** Initial linear gain. Default 1. */
  gain?: number;
};

/** Optional soft limiter inserted between the destination-bound buses and the speakers. */
export type CompressorSpec = {
  threshold: number;
  knee: number;
  ratio: number;
  attack: number;
  release: number;
};

export type BusTopology = {
  /** Declared parents-before-children (a parent must appear before any bus that references it). */
  buses: BusSpec[];
  masterCompressor?: CompressorSpec;
};

export type BusNode = {
  id: BusId;
  gain: GainNode;
};

export type BusGraph = {
  byId: Map<BusId, BusNode>;
  destination: AudioNode;
};

export function createBusGraph(ctx: AudioContext, topology: BusTopology): BusGraph {
  const dest = ctx.destination;
  const byId = new Map<BusId, BusNode>();

  // Buses routing to "destination" feed the compressor (if present), which feeds the speakers.
  let routeTarget: AudioNode = dest;
  if (topology.masterCompressor !== undefined) {
    const m = topology.masterCompressor;
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = m.threshold;
    compressor.knee.value = m.knee;
    compressor.ratio.value = m.ratio;
    compressor.attack.value = m.attack;
    compressor.release.value = m.release;
    compressor.connect(dest);
    routeTarget = compressor;
  }

  for (const spec of topology.buses) {
    const parentNode = spec.parent === "destination" ? routeTarget : byId.get(spec.parent)?.gain;
    if (parentNode === undefined) {
      throw new Error(
        `bus "${spec.id}" references unknown parent "${spec.parent}" — declare parents before children`,
      );
    }
    const gain = ctx.createGain();
    gain.gain.value = spec.gain ?? 1;
    gain.connect(parentNode);
    byId.set(spec.id, { id: spec.id, gain });
  }

  return { byId, destination: dest };
}
